import { Loader2 } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { memo, useCallback, useState } from "react"
import { useTweetFeedState } from "../../hooks/tweets/useTweetFeedState"
import { useTweets } from "../../hooks/tweets/useTweets"
import type { TweetSortBy } from "../../lib/types"
import { TweetCard } from "../market/TweetCard"
import { TweetSortToggle } from "../market/TweetSortToggle"
import { STAGGER_EASE } from "../market/tweetConstants"

interface TrendingTweetsProps {
	/** Controlled sort — when provided, sort toggle is NOT rendered (parent owns it) */
	sortBy?: TweetSortBy
	onSortChange?: (sort: TweetSortBy) => void
	/** Stable lower-bound timestamp (ms) for the tweet query — e.g. week open time */
	sinceMs?: number | null
}
const LOAD_MORE_COUNT = 20

export const TrendingTweets = memo(function TrendingTweets({
	sortBy: controlledSortBy,
	onSortChange: controlledOnSortChange,
	sinceMs,
}: TrendingTweetsProps) {
	// Internal state used when no controlled props are provided (e.g. home page)
	const [internalSortBy, setInternalSortBy] = useState<TweetSortBy>("recent")
	const isControlled = controlledSortBy !== undefined
	const sortBy = isControlled ? controlledSortBy : internalSortBy

	const { results, status, loadMore, isLoading, hasMore } = useTweets(
		sinceMs != null ? { sortBy, sinceMs } : null,
	)

	const {
		contentRef,
		frozenHeight,
		captureFrozenHeight,
		handleLoadMore,
		hasLoadedOnce,
		isSwitchingSort,
		isLoadingMore,
		isWaiting,
		isEmpty,
	} = useTweetFeedState({
		results,
		status,
		isLoading,
		loadMore,
		loadMoreCount: LOAD_MORE_COUNT,
	})

	const handleSortChange = useCallback(
		(sort: TweetSortBy) => {
			captureFrozenHeight()
			if (controlledOnSortChange) {
				controlledOnSortChange(sort)
			} else {
				setInternalSortBy(sort)
			}
		},
		[controlledOnSortChange, captureFrozenHeight],
	)

	return (
		<div>
			{/* Only render toggle when uncontrolled (home page) — timeline page owns its own */}
			{!isControlled && (
				<div className="mb-4">
					<TweetSortToggle onSortChange={handleSortChange} sortBy={sortBy} />
				</div>
			)}

			<div ref={contentRef} style={frozenHeight ? { minHeight: frozenHeight } : undefined}>
				<AnimatePresence mode="wait">
					{isWaiting ? null : isEmpty ? (
						<motion.div
							animate={{ opacity: 1, scale: 1 }}
							className="w-full py-12 text-center"
							initial={{ opacity: 0, scale: 0.95 }}
							key="empty"
							transition={{ duration: 0.2, ease: "easeOut" }}
						>
							<p className="font-medium text-text-muted">No tweets yet</p>
							<p className="mt-2 text-sm text-text-muted">
								Check back later for the latest from top creators.
							</p>
						</motion.div>
					) : (
						<motion.div
							animate={{ opacity: isSwitchingSort ? 0.5 : 1 }}
							initial={hasLoadedOnce ? { opacity: 0.5 } : { opacity: 0 }}
							key="content"
							transition={{ duration: 0.25 }}
						>
							<div className="space-y-3">
								{results.map((tweet, index) => (
									<motion.div
										animate={{ opacity: 1, y: 0 }}
										initial={{ opacity: 0, y: 12 }}
										key={tweet.tweetId}
										transition={{
											duration: 0.35,
											delay: Math.min(index, 10) * 0.04,
											ease: STAGGER_EASE,
										}}
									>
										<TweetCard
											avatarUrl={tweet.avatarUrl}
											createdAtMs={tweet.createdAtMs}
											displayName={tweet.displayName}
											handle={tweet.handle}
											likeCount={tweet.likeCount}
											marketAddress={tweet.marketAddress}
											quoteCount={tweet.quoteCount}
											replyCount={tweet.replyCount}
											retweetCount={tweet.retweetCount}
											text={tweet.text}
											tweetId={tweet.tweetId}
											viewCount={tweet.viewCount}
										/>
									</motion.div>
								))}
							</div>

							{/* Load more button */}
							{hasMore && (
								<div className="flex justify-center pt-4">
									<button
										className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-bg-card px-5 py-2.5 font-medium text-sm text-text-secondary shadow-sm transition-all hover:border-border hover:bg-bg-secondary hover:text-text-primary active:scale-[0.97] disabled:opacity-50"
										disabled={isLoadingMore}
										onClick={handleLoadMore}
										type="button"
									>
										{isLoadingMore ? (
											<>
												<Loader2 aria-hidden="true" className="size-4 animate-spin" />
												<span>Loading…</span>
											</>
										) : (
											<span>Load more tweets</span>
										)}
									</button>
								</div>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	)
})
